import { useEffect, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import dayjs from 'dayjs';
import { Loading } from '@appkit4/react-components/loading';
import { Table, Column } from '@appkit4/react-components/table';
import { Pagination } from '@appkit4/react-components/pagination';
import { Panel } from '@appkit4/react-components/panel';
import { Notification, toaster } from '@appkit4/react-components/notification';
import { getTokenData, getAccessInfo } from '@config/getAnalyticsApis';
import {
    appToTableColumnMap,
    titleMap,
    COMPLETION_REQ_UNIT_COST,
    COMPLETION_RES_UNIT_COST,
    distributionPieOptions,
    dailyUsageOptions,
    initialTableData,
    applicationToColorMap,
    dailyCostEstimationOptions,
} from './constants';
import { findUserWithMostTokens, calculateTokenUsageDaily, usePrevious } from './utils';
import type { TokenDataContainerProps, TokenData, ChartSeries, ChartDataPoint } from './types';
import styles from './Analytics.module.scss';

const ITEMS_PER_PAGE = 15;

/**
 * Fill missing dates in chart series data with zeros
 */
const fillMissingDates = (series: ChartSeries): ChartSeries => {
    const timestamps = series.data.map((item) => item[0]);
    const minDate = Math.min(...timestamps);
    const maxDate = Math.max(...timestamps);

    const dateMap = new Map(series.data.map((item) => [item[0], item[1]]));
    const filledData: ChartDataPoint[] = [];

    for (let date = minDate; date <= maxDate; date += 86400000) {
        // 86400000 ms = 1 day
        if (dateMap.has(date)) {
            filledData.push([date, dateMap.get(date)!]);
        } else {
            filledData.push([date, 0]);
        }
    }

    return {
        ...series,
        data: filledData,
    };
};

/**
 * Format number with commas for readability
 */
const formatNumberWithCommas = (number: number): string => {
    return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const TokenDataContainer: React.FC<TokenDataContainerProps> = ({ parentSetUserList, filter }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [totalTokenUsage, setTotalTokenUsage] = useState(0);
    const [mostActiveUser, setMostActiveUser] = useState('');
    const [mostActiveApplication, setMostActiveApplication] = useState('');
    const [options, setOptions] = useState(dailyUsageOptions);
    const [pieOptions, setPieOptions] = useState(distributionPieOptions);
    const [costBarOptions, setCostBarOptions] = useState(dailyCostEstimationOptions);
    const [tableData, setTableData] = useState([...initialTableData]);
    const [activityTableData, setActivityTableData] = useState<TokenData[]>([]);
    const [completionTokenList, setCompletionTokenList] = useState<TokenData[]>([]);
    const [userCompletionList, setUserCompletionList] = useState<TokenData[][]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const prevFilter = usePrevious(filter);

    /**
     * Calculate token distribution by application for pie chart
     */
    const calculateTokensDistributionData = (data: TokenData[]) => {
        const tokenUsage: Record<string, number> = {};
        let totalTokens = 0;

        data.forEach((item) => {
            const projectType = item.project_type;
            const itemTotalTokens = item.request_token_count + item.response_token_count;
            totalTokens += itemTotalTokens;

            if (tokenUsage[projectType]) {
                tokenUsage[projectType] += itemTotalTokens;
            } else {
                tokenUsage[projectType] = itemTotalTokens;
            }
        });

        setTotalTokenUsage(totalTokens);

        let maxUsageApp = '';
        let maxToken = 0;

        const result = Object.keys(tokenUsage).map((key) => {
            if (tokenUsage[key] > maxToken) {
                maxUsageApp = key;
                maxToken = tokenUsage[key];
            }
            return {
                name: titleMap[key] || key,
                color: applicationToColorMap[key],
                y: parseFloat(((tokenUsage[key] / totalTokens) * 100).toFixed(2)),
            };
        });

        setMostActiveApplication(titleMap[maxUsageApp] || maxUsageApp);
        return result;
    };

    /**
     * Get total cost from table data
     */
    const getTotalCost = (tableData: typeof initialTableData): string => {
        if (tableData && tableData[4]) {
            return tableData[4].estimationCost.toFixed(2);
        }
        return '0';
    };

    /**
     * Update chart options with filtered data
     */
    const updateOptions = (completionData: TokenData[], tokensListByUser: TokenData[][]) => {
        // Extract and deduplicate user list
        let userList = tokensListByUser
            ?.map((item) => {
                let sum = 0;
                for (const i of item) {
                    if (i.project_type === filter.application || filter.application === 'All' || !filter.application) {
                        sum += i.request_token_count + i.response_token_count;
                    }
                }
                if (sum > 0) {
                    return item[0].user_email;
                }
            })
            .filter((item) => item) as string[];

        userList = [...new Set(userList)];
        parentSetUserList(userList);

        // Filter data by application and user
        const filterByApplicationAndUser = (data: TokenData[]) => {
            return data
                .filter((item) => {
                    if (!filter.application || filter.application === 'All') {
                        return true;
                    }
                    return item.project_type === filter.application;
                })
                .filter((item) => {
                    if (!filter.selectedUserList || filter.selectedUserList.length === 0) {
                        return true;
                    }
                    return filter.selectedUserList.indexOf(item.user_email) !== -1;
                });
        };

        // Completion tokens series
        const completionSeries = fillMissingDates({
            name: 'Token',
            color: '#415385',
            data: calculateTokenUsageDaily(filterByApplicationAndUser(completionData), 'Tokens').map((item) => [
                new Date(item.Date).getTime(),
                item.tokenUsageEachDay,
            ]),
        });

        // Token distribution pie series
        const tokenDistributionPieSeries = {
            type: 'pie' as const,
            name: 'Percentage',
            colorByPoint: true,
            data: calculateTokensDistributionData(filterByApplicationAndUser(completionData)),
        };

        // Completion cost series
        const completionCostSeries = fillMissingDates({
            name: 'Completion Cost',
            color: '#415385',
            data: calculateTokenUsageDaily(
                completionData.filter((item) => {
                    if (!filter.application || filter.application === 'All') {
                        return true;
                    }
                    return item.project_type === filter.application;
                }),
                'CompletionCost'
            ).map((item) => [new Date(item.Date).getTime(), item.tokenUsageEachDay]),
        });

        // Update chart options
        setOptions({
            ...options,
            series: [completionSeries],
            title: {
                text: 'Token Usage Data',
                align: 'center',
            },
        });

        setPieOptions({
            ...pieOptions,
            series: [tokenDistributionPieSeries],
        });

        setCostBarOptions({
            ...costBarOptions,
            series: [completionCostSeries],
            title: {
                text: 'Daily Cost Estimation',
                align: 'center',
            },
        });
    };

    /**
     * Fetch token data from backend
     */
    const getTokens = async () => {
        setIsLoading(true);

        try {
            // Authenticate
            const accessInfo = await getAccessInfo();
            if (!accessInfo.success) {
                throw new Error('Authentication failed');
            }

            // For now, we'll fetch from single endpoint
            // TODO: Update to fetch from multiple environments if needed
            const response = await getTokenData({
                start_time: filter.startTime.format('YYYY-MM-DD HH:mm:ss.SSS'),
                end_time: filter.endTime.format('YYYY-MM-DD HH:mm:ss.SSS'),
            });

            // Process response - backend returns { success, response, tokenUsageList }
            if (!response.success) {
                throw new Error('Failed to fetch token data');
            }

            const tokenList: TokenData[] = Array.isArray(response.tokenUsageList) ? response.tokenUsageList : [];

            const completionTokenList = tokenList.filter((item) => item.api_type === 'Completion');

            // Group by user email
            const responseByUserList: TokenData[][] = [];
            const userMap: Record<string, TokenData[]> = {};

            tokenList.forEach((item) => {
                if (!userMap[item.user_email]) {
                    userMap[item.user_email] = [];
                }
                userMap[item.user_email].push(item);
            });

            Object.values(userMap).forEach((userTokens) => {
                responseByUserList.push(userTokens);
            });

            setIsLoading(false);

            return {
                _completionTokenList: completionTokenList,
                _responseByUserList: responseByUserList,
            };
        } catch (error) {
            console.error('Error fetching token data:', error);
            toaster.notify(<Notification status="error" title="Error" message="Failed to load analytics data" />);
            setIsLoading(false);
            return {
                _completionTokenList: [],
                _responseByUserList: [],
            };
        }
    };

    /**
     * Calculate tokens for table data
     */
    function calculateTokens(...tokensListGroup: TokenData[][]) {
        const completionDataGroupAllEnv = tokensListGroup.map((item) => {
            return item
                .filter((item) => item.api_type === 'Completion')
                .filter((item) => {
                    if (!filter.application || filter.application === 'All') {
                        return true;
                    }
                    return item.project_type === filter.application;
                })
                .filter((item) => {
                    if (!filter.selectedUserList || filter.selectedUserList.length === 0) {
                        return true;
                    }
                    return filter.selectedUserList.indexOf(item.user_email) !== -1;
                });
        });

        const originalData = [...initialTableData];

        for (const [key] of Object.entries(titleMap)) {
            const completionDataGroup =
                key === 'All'
                    ? completionDataGroupAllEnv
                    : completionDataGroupAllEnv.map((item) => {
                          return item.filter((item) => item.project_type === key);
                      });

            const tokenUsage = completionDataGroup.reduce((acc, item) => {
                return acc + item.reduce((acc, item) => acc + item.request_token_count + item.response_token_count, 0);
            }, 0);

            const estimationCost = completionDataGroup.reduce((acc, item) => {
                return (
                    acc +
                    item.reduce(
                        (acc, item) =>
                            acc +
                            item.request_token_count * COMPLETION_REQ_UNIT_COST(item.model_name || '') +
                            item.response_token_count * COMPLETION_RES_UNIT_COST(item.model_name || ''),
                        0
                    )
                );
            }, 0);

            const nextData = { tokenUsage, estimationCost };
            const columnIndex = appToTableColumnMap[key];
            if (columnIndex !== undefined && originalData[columnIndex]) {
                originalData[columnIndex] = {
                    ...originalData[columnIndex],
                    ...nextData,
                };
            }
        }

        return originalData;
    }

    /**
     * Update graphs and table with new data
     */
    const updateGraphAndTable = (completionTokenList: TokenData[], responseByUserList: TokenData[][]) => {
        if (!completionTokenList || !responseByUserList) return;

        const { user } = findUserWithMostTokens(responseByUserList, filter.application);
        setMostActiveUser(user || '');

        updateOptions(completionTokenList, responseByUserList);
        setTableData(calculateTokens(completionTokenList));

        const filteredActivityData = completionTokenList
            .filter((item) => {
                if (!filter.application || filter.application === 'All') {
                    return true;
                }
                return item.project_type === filter.application;
            })
            .filter((item) => {
                if (!filter.selectedUserList || filter.selectedUserList.length === 0) {
                    return true;
                }
                return filter.selectedUserList.indexOf(item.user_email) !== -1;
            });

        setActivityTableData(filteredActivityData);
    };

    /**
     * Initial data load
     */
    useEffect(() => {
        getTokens().then((res) => {
            const { _completionTokenList, _responseByUserList } = res;
            setCompletionTokenList(_completionTokenList);
            setUserCompletionList(_responseByUserList);
            updateGraphAndTable(_completionTokenList, _responseByUserList);
            setCurrentPage(1);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Handle filter changes
     */
    useEffect(() => {
        if (prevFilter) {
            const { startTime, endTime } = prevFilter;
            if (!(startTime.isSame(filter.startTime, 'day') && endTime.isSame(filter.endTime, 'day'))) {
                // Date changed, refetch data
                getTokens().then((res) => {
                    const { _completionTokenList, _responseByUserList } = res;
                    setCompletionTokenList(_completionTokenList);
                    setUserCompletionList(_responseByUserList);
                    updateGraphAndTable(_completionTokenList, _responseByUserList);
                });
            } else {
                // Only filters changed, reprocess existing data
                updateGraphAndTable(completionTokenList, userCompletionList);
            }
            setCurrentPage(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter]);

    /**
     * Handle page change
     */
    const onPageChange = (page: number) => {
        setCurrentPage(page);
    };

    // Paginate activity table data
    const paginatedData = activityTableData.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

    return (
        <div>
            {/* Metrics Panels */}
            <div className={styles['data-panel-grp']}>
                <div className={styles['ap-panel-container']}>
                    <Panel bordered title="Total Cost">
                        <div className={styles['panel-content']}>
                            $ {formatNumberWithCommas(Number(getTotalCost(tableData)))}
                        </div>
                    </Panel>
                </div>
                <div className={styles['ap-panel-container']}>
                    <Panel bordered title="Total Tokens Used">
                        <div className={styles['panel-content']}>{formatNumberWithCommas(totalTokenUsage)}</div>
                    </Panel>
                </div>
                <div className={styles['ap-panel-container']}>
                    <Panel bordered title="Average Daily Usage">
                        <div className={styles['panel-content']}>
                            {formatNumberWithCommas(
                                Math.floor(
                                    totalTokenUsage / (dayjs(filter.endTime).diff(dayjs(filter.startTime), 'day') + 1)
                                )
                            )}
                        </div>
                    </Panel>
                </div>
                <div className={styles['ap-panel-container']}>
                    <Panel bordered title="Most Active Team Member">
                        <div className={styles['panel-content']} style={{ fontSize: 16 }}>
                            {mostActiveUser || 'N/A'}
                        </div>
                    </Panel>
                </div>
                <div className={styles['ap-panel-container']}>
                    <Panel bordered title="Most Used AI Tool">
                        <div className={styles['panel-content']} style={{ fontSize: 16 }}>
                            {mostActiveApplication || 'N/A'}
                        </div>
                    </Panel>
                </div>
            </div>

            {/* Charts Section */}
            <div className={styles['token-data-container']}>
                {isLoading && (
                    <div className={styles['mask-overlay']}>
                        <Loading size="lg" />
                        &nbsp;Loading Data ...
                    </div>
                )}

                <div className={styles['chart-grp-container']}>
                    <Panel className={styles['chart-container']}>
                        <HighchartsReact highcharts={Highcharts} options={options} />
                    </Panel>
                    <Panel className={styles['chart-container']}>
                        <HighchartsReact highcharts={Highcharts} options={pieOptions} />
                    </Panel>
                </div>

                <div>
                    <Panel className={styles['chart-container']}>
                        <HighchartsReact highcharts={Highcharts} options={costBarOptions} />
                    </Panel>
                </div>
            </div>

            {/* Activity Table */}
            <div className={styles['table-section']}>
                <Panel className={styles['activity-table-panel']}>
                    <h1 className={styles['table-title']}>Activity Table</h1>
                    <Table striped originalData={paginatedData} hasTitle>
                        <Column
                            field="created_at"
                            renderCell={(item: TokenData) => {
                                return dayjs(item.created_at).format('YYYY-MM-DD HH:mm:ss');
                            }}>
                            Date
                        </Column>
                        <Column field="user_email">Team Member</Column>
                        <Column
                            field="project_type"
                            renderCell={(item: TokenData) => {
                                return titleMap[item.project_type] || item.project_type;
                            }}>
                            AI Tool
                        </Column>
                        <Column
                            field="request_token_count"
                            renderCell={(item: TokenData) => {
                                return item.request_token_count + item.response_token_count;
                            }}>
                            Token Used
                        </Column>
                        <Column field="api_type">Action</Column>
                    </Table>
                    <div className={styles['pagination-container']}>
                        <Pagination
                            current={currentPage}
                            total={Math.ceil(activityTableData.length / ITEMS_PER_PAGE)}
                            onPageChange={onPageChange}
                        />
                    </div>
                </Panel>
            </div>
        </div>
    );
};

export default TokenDataContainer;
